export interface YoutubeClip {
  id: string
  title: string
  url: string
  description: string
  occurredAt: string
  tags: string[]
  thumbnail?: string
  isShort?: boolean
}

export function youtubeDayUrl(
  endpoint: string,
  day: string,
  base = 'https://vod.ohn.sh',
) {
  const url = new URL(endpoint, base)
  if (!['https:', 'http:'].includes(url.protocol)) {
    throw new Error('Invalid YouTube endpoint URL')
  }
  url.searchParams.set('day', day)
  return url.href
}

export function parseYoutubeClips(raw: unknown): YoutubeClip[] {
  if (!Array.isArray(raw)) throw new Error('Invalid YouTube response')
  const clips: YoutubeClip[] = []
  for (const item of raw) {
    if (!item || typeof item !== 'object') continue
    const record = item as Record<string, unknown>
    if (
      typeof record.id !== 'string' ||
      !record.id ||
      typeof record.title !== 'string' ||
      !record.title ||
      typeof record.url !== 'string'
    ) {
      continue
    }
    let parsedUrl: URL
    try {
      parsedUrl = new URL(record.url)
    } catch {
      continue
    }
    if (!['https:', 'http:'].includes(parsedUrl.protocol)) continue
    let thumbnail: string | undefined
    if (typeof record.thumbnail === 'string' && record.thumbnail) {
      try {
        const thumbUrl = new URL(record.thumbnail)
        if (['https:', 'http:'].includes(thumbUrl.protocol)) thumbnail = thumbUrl.href
      } catch {
        // Ignore malformed thumbnail URLs.
      }
    }
    const tags = Array.isArray(record.tags)
      ? record.tags.filter(
          (tag): tag is string => typeof tag === 'string' && Boolean(tag.trim()),
        )
      : []
    clips.push({
      id: record.id,
      title: record.title,
      url: parsedUrl.href,
      description: typeof record.description === 'string' ? record.description : '',
      occurredAt: typeof record.occurredAt === 'string' ? record.occurredAt : '',
      tags,
      ...(thumbnail ? { thumbnail } : {}),
      ...(typeof record.isShort === 'boolean' ? { isShort: record.isShort } : {}),
    })
  }
  return clips
}

export async function fetchYoutubeClips(
  endpoint: string,
  day: string,
  signal?: AbortSignal,
): Promise<YoutubeClip[]> {
  const base = typeof location !== 'undefined' ? location.origin : 'https://vod.ohn.sh'
  const url = youtubeDayUrl(endpoint, day, base)
  const response = await fetch(url, { signal, credentials: 'omit' })
  if (!response.ok) {
    await response.body?.cancel()
    throw new Error(`YouTube request failed (${response.status})`)
  }
  return parseYoutubeClips(await response.json())
}
